import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Scan,
  Thermometer,
  Calculator,
  FileText,
  Zap,
  ShieldCheck,
  CheckCircle2,
  Cpu,
  Layers,
  Wrench,
  ChevronDown,
} from 'lucide-react';

// Multimodal inspection modes for the interactive hero widget
type InspectionMode = 'visual' | 'thermal' | 'pec' | 'ffs';

interface HeroDemoData {
  title: string;
  badge: string;
  badgeType: 'info' | 'warning' | 'danger' | 'success';
  description: string;
  metrics: { label: string; value: string; note?: string }[];
  details: string[];
}

const DEMO_MODES: Record<InspectionMode, HeroDemoData> = {
  visual: {
    title: 'Drone RGB Visual Defect Segmentation',
    badge: '3 DEFECTS IDENTIFIED',
    badgeType: 'danger',
    description: 'Deep neural networks segment weather jacketing tears, sealant degradation, and banding gaps down to 1.5mm resolution.',
    metrics: [
      { label: 'Jacketing Tear', value: '98.4%', note: 'Confidence' },
      { label: 'Sealant Breakdown', value: '94.2%', note: 'Flange Joint #2' },
      { label: 'Water Entry Risk', value: 'CRITICAL', note: 'Low Point Trapping' },
    ],
    details: [
      'Model: YOLOv8-Industrial-Segmentation (Calibrated for SS & Al cladding)',
      'Defect Area: 142 cm² along horizontal run',
      'Recommended Action: Localized weather-patching without scaffolding strip',
    ],
  },
  thermal: {
    title: 'Radiometric IR Moisture Intrusion Mapping',
    badge: 'WET POCKET DETECTED',
    badgeType: 'warning',
    description: 'Isothermal differential analysis detects evaporative cooling and sensible heat retention in saturated insulation.',
    metrics: [
      { label: 'Delta T (ΔT)', value: '+8.4 °C', note: 'vs Ambient Sound Cladding' },
      { label: 'Moisture Extent', value: '1.25 m', note: 'Axial length' },
      { label: 'Insulation State', value: 'WET (CaSil)', note: 'Loss of thermal barrier' },
    ],
    details: [
      'Radiometric FLIR IR Sensor calibrated to 0.03°C NETD sensitivity',
      'Solar solar-load normalized using ambient weather station data',
      'API RP 583 Temperature Window: 68°C (Critical CUI Vulnerability Zone)',
    ],
  },
  pec: {
    title: 'Pulsed Eddy Current (PEC) Wall Loss Scan',
    badge: 'LOCAL METAL LOSS: 44.3%',
    badgeType: 'warning',
    description: 'Through-insulation electromagnetic screening measures remaining steel wall thickness without removing cladding.',
    metrics: [
      { label: 'Nominal Wall (t_nom)', value: '12.7 mm', note: 'Sch 80 Carbon Steel' },
      { label: 'Min Measured (t_min)', value: '7.07 mm', note: 'Deepest pit location' },
      { label: 'Loss Percentage', value: '44.3%', note: 'Local wall reduction' },
    ],
    details: [
      'Probing Depth: Screened through 75mm Calcium Silicate + 0.8mm Al jacketing',
      'Footprint: 85mm electromagnetic spot size with ±0.3mm accuracy',
      'Direct input ingested into API 579-1 Part 5 deterministic engine',
    ],
  },
  ffs: {
    title: 'API 579-1 / ASME FFS-1 Level 1 Evaluation',
    badge: 'PASS - FULL MAWP (2.41 MPa)',
    badgeType: 'success',
    description: 'Deterministic Remaining Strength Factor (RSF) calculation confirms asset is fit for continued safe operation.',
    metrics: [
      { label: 'Calculated RSF', value: '0.932', note: 'Threshold RSFa = 0.900' },
      { label: 'Folias Factor (Mt)', value: '1.348', note: 'Bulging coefficient' },
      { label: 'Status', value: 'PASS MAWP', note: 'No derating mandatory' },
    ],
    details: [
      'Deterministic Equation: RSF = Rt / (1 - (1/Mt)(1 - Rt))',
      'Future Corrosion Allowance (FCA): 1.5mm accounted over 5-year run',
      'Regulatory: PE-stamped automated compliance record generated',
    ],
  },
};

const stats = [
  { value: '40-60%', label: 'of all piping failures in process units caused by CUI', source: 'API RP 583 / NACE' },
  { value: '60-80%', label: 'of stripped insulation found completely dry upon removal', source: 'Industry Integrity Audits' },
  { value: 'Up to 65%', label: 'reduction in direct scaffolding and turnaround expenses', source: 'Verified Customer Benchmarks' },
  { value: '< 120s', label: 'from raw sensor capture to certified Level 1 FFS calculation', source: 'Deterministic Execution Engine' },
];

const capabilities = [
  {
    icon: Scan,
    title: 'Cladding Defect Detection',
    badge: 'Computer Vision AI',
    description: 'Automated deep-learning segmentation of weather jacketing punctures, seam unzipping, sealant degradation, and banding failure from drone and handheld RGB feeds.',
    points: ['Pixel-accurate mask generation', 'Sub-millimeter breach detection', 'Banding & sealant integrity scoring'],
  },
  {
    icon: Thermometer,
    title: 'Radiometric IR Moisture Mapping',
    badge: 'Thermal NDT',
    description: 'Isothermal differential analysis maps hidden moisture pockets behind aluminium and stainless-steel cladding without removing a single square meter of insulation.',
    points: ['Atmospheric solar normalization', 'Evaporative cooling pattern analysis', 'Calibrated thermal susceptibility indices'],
  },
  {
    icon: Calculator,
    title: 'API 579-1 Part 5 FFS Engine',
    badge: 'Deterministic Math',
    description: 'Zero hallucination risk. Direct implementation of ASME/API 579-1 Level 1 Remaining Strength Factor (RSF), Folias bulging factor, and MAWP calculations.',
    points: ['Strict deterministic equations', 'Automatic MAWP derating calculations', 'PE audit-ready PDF dossiers'],
  },
  {
    icon: BarChart3,
    title: 'Inspection Priority Score (IPS)',
    badge: 'Risk-Based Inspection',
    description: 'Synthesizes API RP 583 temperature windows (-4°C to 120°C), geometric water traps, coastal marine salinity, and historical CUI events into actionable rankings.',
    points: ['API 581 RBI compatibility', 'Identifies dead legs & low-point traps', 'Multi-factor hazard scoring matrix'],
  },
  {
    icon: FileText,
    title: 'Legacy PDF & NDT Digitization',
    badge: 'Document Intelligence',
    description: 'Converts decades of unstructured PDF inspection reports, ultrasonic thickness logs, and isometric drawings into structured database records.',
    points: ['Automated tabular data extraction', 'Historical corrosion rate tracking', 'Asset tag & circuit matching'],
  },
  {
    icon: Wrench,
    title: 'Bidirectional CMMS Integration',
    badge: 'Enterprise Dispatch',
    description: 'Push high-priority findings directly into SAP Plant Maintenance (PM) or IBM Maximo with photographic evidence, GPS coordinates, and bill of materials.',
    points: ['Zero manual double-entry', 'Pre-populated repair orders', 'Closed-loop verification tracking'],
  },
];

const workflowSteps = [
  {
    num: '01',
    title: 'Multimodal Screening Upload',
    subtitle: 'Drone, Handheld & NDT Sensors',
    description: 'Ingest visual drone imagery, radiometric infrared thermography, pulsed eddy current (PEC) scans, or legacy PDF inspection dossiers into secure encrypted vaults.',
    spec: 'FLIR radiometric, DJI Enterprise, PEC CSV, UT logs',
  },
  {
    num: '02',
    title: 'AI Anomaly Segmentation',
    subtitle: 'Calibrated Industrial Models',
    description: 'Proprietary computer vision pipelines detect jacketing breaches while thermal algorithms identify subsurface moisture pockets and calculate thermal susceptibility.',
    spec: 'Confidence thresholds, area estimation, severity grading',
  },
  {
    num: '03',
    title: 'Deterministic Engineering Analysis',
    subtitle: 'API 579-1 & API 581 Equations',
    description: 'Critical findings undergo automated API 579-1 Level 1 fitness-for-service math to determine exact Remaining Strength Factor (RSF) and maximum allowable pressure.',
    spec: 'Strict deterministic code; no AI guesswork in safety calculations',
  },
  {
    num: '04',
    title: 'Targeted Work Order Dispatch',
    subtitle: 'SAP PM & IBM Maximo',
    description: 'Engineers review ranked priorities on interactive isometrics, approve recommendations, and dispatch surgical work orders with zero wasted scaffolding.',
    spec: 'Direct CMMS API synchronization with full audit trail',
  },
];

const comparisonData = [
  {
    feature: 'Inspection Strategy',
    traditional: 'Blanket strip 10-30% of insulation blindly during turnarounds',
    insuscan: 'Non-destructive targeted screening; strip only verified wet/damaged areas',
  },
  {
    feature: 'Cost per Location',
    traditional: '€15,000 – €45,000 (scaffolding, stripping, disposal, re-insulation)',
    insuscan: '€1,200 – €3,800 (drone + radiometric screening + targeted intervention)',
  },
  {
    feature: 'Unnecessary Stripping',
    traditional: '60-80% of stripped insulation is found to be in pristine condition',
    insuscan: '< 5% unnecessary stripping rate with multi-sensor validation',
  },
  {
    feature: 'Turnaround Downtime',
    traditional: 'Weeks of scaffolding erection causing process bottleneck',
    insuscan: 'Rapid screening conducted on-stream without process interruption',
  },
  {
    feature: 'Safety & Working at Height',
    traditional: 'High man-hour exposure to scaffolding, asbestos risk & hot piping',
    insuscan: 'Up to 75% reduction in high-risk scaffolding man-hours',
  },
  {
    feature: 'Fitness-for-Service',
    traditional: 'Manual spreadsheets prone to transcription errors',
    insuscan: 'Automated deterministic API 579-1 Level 1 calculations',
  },
];

const faqs = [
  {
    q: 'How does InsuScan AI avoid AI hallucinations in safety-critical decisions?',
    a: 'InsuScan AI enforces strict separation between AI perception and engineering calculation. AI models are used solely for sensory classification (e.g. segmenting a jacketing tear in an image or classifying thermal gradients). Once thickness measurements and dimensions are extracted, all Fitness-for-Service (FFS) and Remaining Strength Factor (RSF) calculations are executed using 100% deterministic Python engineering code implementing the exact formulas from API 579-1 / ASME FFS-1 Part 5. No generative AI or probabilistic model ever touches the safety calculations.',
  },
  {
    q: 'What sensor hardware and drone formats are supported?',
    a: 'We support standard radiometric infrared formats including FLIR (.rjpeg, .seq, raw radiometric thermal matrices), standard optical RGB imagery from DJI Enterprise (Matrice 300/350 RTK, Mavic 3 Enterprise), handheld cameras, as well as pulsed eddy current (PEC) thickness grid exports and ultrasonic (UT) data sheets.',
  },
  {
    q: 'How does the CMMS integration work with SAP PM and IBM Maximo?',
    a: 'InsuScan AI provides bidirectional REST API and webhook connectors for SAP Plant Maintenance (BAPI / OData) and IBM Maximo. When an integrity engineer approves a finding, InsuScan AI automatically generates a maintenance notification or work order with exact GPS / isometric coordinates, photographic evidence, defect classification, and recommended scope of work.',
  },
  {
    q: 'Can InsuScan AI be deployed on-premises or in private cloud for air-gapped refineries?',
    a: 'Yes. InsuScan AI is built using Docker container architecture and can be deployed fully on-premises in isolated private cloud environments (OpenShift, Kubernetes, bare-metal Linux) with no outbound internet connectivity required.',
  },
  {
    q: 'How does InsuScan AI calculate Scaffolding ROI?',
    a: 'Industrial facilities typically budget €100-€300 per linear meter for scaffolding, insulation stripping, hazardous disposal, and re-jacketing. By screening assets on-stream and demonstrating that 60-80% of suspected lines have intact barriers and dry insulation, operators can bypass unnecessary stripping campaigns and direct scaffolding solely to confirmed defect areas.',
  },
];

export function LandingPage() {
  const [activeDemoMode, setActiveDemoMode] = useState<InspectionMode>('visual');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  // ROI Calculator interactive state
  const [pipingKm, setPipingKm] = useState<number>(18);
  const [scaffoldingBudget, setScaffoldingBudget] = useState<number>(1400000);
  const [currentStripRate, setCurrentStripRate] = useState<number>(25);

  // Calculation for ROI
  const estimatedStripLocations = Math.round((pipingKm * 1000 * (currentStripRate / 100)) / 10);
  const unnecessaryStrippedLocations = Math.round(estimatedStripLocations * 0.72);
  const estimatedSavings = Math.round(scaffoldingBudget * 0.62);
  const scaffoldingHoursSaved = Math.round(unnecessaryStrippedLocations * 34);

  const currentModeData = DEMO_MODES[activeDemoMode];

  return (
    <div className="min-h-screen bg-white text-slate-900 selection:bg-blue-600 selection:text-white">
      {/* Top Telemetry Notification Banner */}
      <div className="bg-slate-900 text-slate-300 text-xs py-2 px-4 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-green-400 animate-pulse" />
            <span className="font-mono text-slate-300 font-semibold tracking-wide">SYSTEM STATUS: ENGINE v2.4 OPERATIONAL</span>
            <span className="hidden md:inline text-slate-500">|</span>
            <span className="hidden md:inline text-slate-400">API 579-1 / ASME FFS-1 Part 5 Level 1 Certified Calculation Kernel</span>
          </div>
          <div className="flex items-center gap-4 font-mono text-slate-400 text-[11px]">
            <span>SECURITY: ISO 27001 READY</span>
            <span className="hidden sm:inline">|</span>
            <span className="hidden sm:inline">SAP PM & MAXIMO COMPATIBLE</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-2.5 group">
                <img src="/logo.png" alt="InsuScan AI" className="h-8 w-8 object-contain" />
                <div className="flex flex-col">
                  <span className="text-xl font-bold tracking-tight text-slate-900 group-hover:text-blue-600 transition-colors">
                    InsuScan <span className="text-blue-600">AI</span>
                  </span>
                  <span className="text-[10px] uppercase font-mono tracking-wider text-slate-500 -mt-1">
                    NDT Asset Integrity
                  </span>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation Links */}
            <div className="hidden lg:flex items-center gap-7">
              <a href="#features" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">
                Platform Capabilities
              </a>
              <a href="#demo" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">
                Live Simulator
              </a>
              <a href="#workflow" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">
                Engineering Workflow
              </a>
              <a href="#calculator" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">
                Scaffolding ROI
              </a>
              <a href="#standards" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">
                Standards
              </a>
              <Link to="/docs" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">
                Documentation
              </Link>
            </div>

            {/* CTAs */}
            <div className="flex items-center gap-2.5">
              <Link
                to="/login"
                className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-400 active:scale-[0.98] transition-all shadow-xs cursor-pointer"
              >
                Sign In
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] transition-all shadow-sm hover:shadow-md hover:shadow-blue-500/25 border border-blue-500 cursor-pointer group"
              >
                <span>Access Console</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-16 pb-20 overflow-hidden bg-tech-grid border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Column: Value Proposition */}
            <div className="lg:col-span-6 space-y-6 text-left">
              {/* Technical Indicator Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-xs font-semibold">
                <span className="h-2 w-2 rounded-full bg-blue-600" />
                <span>MULTIMODAL CORROSION-UNDER-INSULATION (CUI) INTELLIGENCE</span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.15]">
                Stop Stripping Sound Insulation.
                <span className="block text-blue-600 mt-1">Target CUI With Deterministic Precision.</span>
              </h1>

              {/* Supporting Copy */}
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
                Between 60% and 80% of insulation stripped during plant turnarounds is completely dry. 
                InsuScan AI merges drone RGB imagery, radiometric infrared thermography, and pulsed eddy current (PEC) data 
                with deterministic <strong>API 579-1 Level 1 calculations</strong> to cut scaffolding and inspection costs by up to 65%.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4 pt-2">
                <a href="#demo" className="btn btn-primary btn-lg shadow-md shadow-blue-600/10">
                  <span>Explore Live Inspection Demo</span>
                  <ArrowRight className="h-5 w-5" />
                </a>
                <a href="#calculator" className="btn btn-secondary btn-lg">
                  <Calculator className="h-5 w-5 text-slate-600" />
                  <span>Calculate Plant ROI</span>
                </a>
              </div>

              {/* Trust Badges */}
              <div className="pt-6 border-t border-slate-200/80 flex flex-wrap items-center gap-6 text-xs text-slate-600">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-blue-600" />
                  <span>API 579-1 / ASME FFS-1</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-blue-600" />
                  <span>API RP 583 CUI Practice</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-blue-600" />
                  <span>SAP PM & Maximo Ready</span>
                </div>
              </div>
            </div>

            {/* Right Column: High-Impact Refinery Digital Twin Showcase */}
            <div className="lg:col-span-6 space-y-4">
              <div className="relative rounded-2xl overflow-hidden border border-slate-300 shadow-xl group bg-slate-950">
                <img
                  src="/banner.png"
                  alt="InsuScan AI CUI Inspection Intelligence in Industrial Refinery"
                  className="w-full h-72 sm:h-80 object-cover object-center group-hover:scale-102 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/45 to-transparent flex flex-col justify-between p-5 sm:p-6">
                  <div className="flex items-center justify-between">
                    <span className="telemetry-pill bg-emerald-500 text-slate-950 font-bold text-xs shadow-sm flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-slate-950 animate-pulse" />
                      ON-STREAM NDT DEPLOYMENT
                    </span>
                    <span className="text-xs font-mono text-slate-300 bg-slate-900/80 px-2.5 py-1 rounded border border-slate-700 backdrop-blur-xs">
                      ROTTERDAM REFINERY // CRUDE UNIT
                    </span>
                  </div>

                  <div className="space-y-2">
                    <div className="text-base sm:text-lg font-bold text-white tracking-wide">
                      Continuous Screening Without Insulation Stripping
                    </div>
                    <p className="text-xs text-slate-300 font-sans leading-relaxed">
                      Drone vision and radiometric FLIR thermography pinpoint moisture pools and guide targeted pulsed eddy current (PEC) wall thickness measurement.
                    </p>
                    <div className="pt-2 flex flex-wrap items-center justify-between gap-2 text-xs font-mono text-emerald-400 border-t border-slate-800">
                      <span>✓ 0m Scaffolding</span>
                      <span>✓ 100% Insulation Preserved</span>
                      <span>✓ API 579-1 Part 5 Level 1</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fast Jump Teaser Card to Dedicated Terminal */}
              <a
                href="#demo"
                className="p-4 rounded-xl border border-blue-200 bg-blue-50/90 hover:bg-blue-100 transition-all flex items-center justify-between group shadow-xs cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-600 text-white shadow-xs">
                    <Cpu className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-blue-950 uppercase tracking-wider">
                      Interactive Multimodal Console Available
                    </div>
                    <div className="text-xs text-blue-700 mt-0.5">
                      Test Visual AI, Thermal IR, PEC Grid & API 579 FFS calculation
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-xs font-bold text-blue-700 group-hover:text-blue-900 group-hover:translate-x-0.5 transition-all">
                  <span>Explore Terminal</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </a>
            </div>

          </div>
        </div>
      </section>

      {/* Industrial Benchmark KPI Section */}
      <section className="py-14 bg-slate-900 text-white border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="border-l-2 border-blue-500 pl-4 space-y-1">
                <div className="text-3xl sm:text-4xl font-extrabold font-mono text-white tracking-tight">
                  {stat.value}
                </div>
                <div className="text-sm font-medium text-slate-300">
                  {stat.label}
                </div>
                <div className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                  {stat.source}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dedicated Interactive Multimodal Inspection Terminal Section */}
      <section id="demo" className="py-20 bg-slate-950 text-white border-b border-slate-800 relative overflow-hidden">
        {/* Subtle grid background */}
        <div className="absolute inset-0 bg-dark-grid opacity-20 pointer-events-none" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-12 space-y-3">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400 font-mono text-xs font-semibold">
              <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
              <span>LIVE MULTIMODAL INSPECTION CONSOLE</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
              Experience Multimodal CUI Screening in Action
            </h2>
            <p className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Explore how InsuScan AI fuses optical cladding damage segmentation, radiometric FLIR moisture mapping, through-jacketing PEC wall loss grids, and deterministic API 579 Level 1 engineering math.
            </p>
          </div>

          {/* Centerpiece Terminal with Full Width & Room to Breathe */}
          <div className="max-w-5xl mx-auto tech-panel-dark rounded-2xl overflow-hidden shadow-2xl border border-slate-700/80 bg-slate-900">
            {/* Simulated Instrument Header */}
            <div className="bg-slate-900 px-5 py-3.5 border-b border-slate-700 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-red-500" />
                <span className="h-3 w-3 rounded-full bg-amber-500" />
                <span className="h-3 w-3 rounded-full bg-green-500" />
                <span className="ml-2 font-mono text-xs text-slate-300 font-semibold tracking-wider">
                  INSPECTOR CONSOLE // ASSET: TK-402A-LINE-12
                </span>
              </div>
              <div className="font-mono text-[11px] text-slate-400 bg-slate-800 px-2.5 py-1 rounded border border-slate-700">
                LAT 51.9244° N | CALIBRATED SENSOR SUITE
              </div>
            </div>

            {/* Inspection Mode Tabs */}
            <div className="bg-slate-800/80 p-2 border-b border-slate-700 grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              <button
                onClick={() => setActiveDemoMode('visual')}
                className={`py-2.5 px-3 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                  activeDemoMode === 'visual'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/60'
                }`}
              >
                <Scan className="h-4 w-4" />
                <span>Visual AI</span>
              </button>
              <button
                onClick={() => setActiveDemoMode('thermal')}
                className={`py-2.5 px-3 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                  activeDemoMode === 'thermal'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/60'
                }`}
              >
                <Thermometer className="h-4 w-4" />
                <span>Thermal IR</span>
              </button>
              <button
                onClick={() => setActiveDemoMode('pec')}
                className={`py-2.5 px-3 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                  activeDemoMode === 'pec'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/60'
                }`}
              >
                <Zap className="h-4 w-4" />
                <span>PEC Grid</span>
              </button>
              <button
                onClick={() => setActiveDemoMode('ffs')}
                className={`py-2.5 px-3 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                  activeDemoMode === 'ffs'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/60'
                }`}
              >
                <Calculator className="h-4 w-4" />
                <span>API 579 FFS</span>
              </button>
            </div>

            {/* Interactive Graphic Canvas with High-Res Real Photos */}
            <div className="relative h-80 sm:h-96 bg-slate-950 flex items-center justify-center overflow-hidden border-b border-slate-700/80">
              <div className="absolute inset-0 bg-dark-grid opacity-20 pointer-events-none" />

              {/* Visual AI Overlay: uses cladding_defect.jpeg */}
              {activeDemoMode === 'visual' && (
                <div className="relative w-full h-full">
                  <img
                    src="/cladding_defect.jpeg"
                    alt="Cladding defect macro tear"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/30" />

                  {/* Real Calibrated Bounding Box over the actual tear */}
                  <div
                    className="absolute border-2 border-red-500 bg-red-500/20 rounded shadow-lg shadow-red-500/20 transition-all animate-pulse"
                    style={{ left: '44%', top: '24%', width: '28%', height: '54%' }}
                  >
                    <div className="absolute -top-6 left-0 bg-red-600 text-white font-mono text-[10px] px-2 py-0.5 rounded font-bold whitespace-nowrap shadow">
                      DEFECT #1: TEAR & SEAM OPENING (98.4%)
                    </div>
                  </div>

                  <div className="absolute top-3 right-3 font-mono text-[11px] text-red-400 bg-slate-900/90 px-2.5 py-1 rounded border border-slate-700">
                    SEVERITY: CRITICAL // RAINWATER INGRESS
                  </div>

                  <div className="absolute bottom-3 inset-x-3 flex items-center justify-between text-xs font-mono bg-slate-900/90 p-2 rounded border border-slate-700 text-slate-300">
                    <span className="flex items-center gap-1.5">
                      <Scan className="h-4 w-4 text-blue-400" />
                      <span>Est. breached area: 142 cm² | Insulation core exposed</span>
                    </span>
                    <span className="text-red-400 font-bold">API 583 HAZARD</span>
                  </div>
                </div>
              )}

              {/* Thermal IR Overlay: uses thermal_flir.jpeg */}
              {activeDemoMode === 'thermal' && (
                <div className="relative w-full h-full">
                  <img
                    src="/thermal_flir.jpeg"
                    alt="Radiometric FLIR Thermogram"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/30" />

                  {/* Thermal Hotspot Crosshair Overlay */}
                  <div
                    className="absolute w-24 h-24 border-2 border-amber-400 rounded-full flex items-center justify-center pointer-events-none"
                    style={{ left: '45%', top: '28%' }}
                  >
                    <div className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-ping" />
                    <div className="absolute -top-6 bg-amber-500 text-slate-950 font-mono text-[10px] px-2 py-0.5 rounded font-bold whitespace-nowrap">
                      SPOT 1: 37.4°C (ΔT = +11.3°C)
                    </div>
                  </div>

                  <div className="absolute top-3 left-3 font-mono text-[11px] text-amber-300 bg-slate-900/90 px-2.5 py-1 rounded border border-slate-700">
                    CALIBRATED LWIR: 8-14μm // IRONBOW PALETTE
                  </div>

                  <div className="absolute bottom-3 inset-x-3 flex items-center justify-between text-xs font-mono bg-slate-900/90 p-2 rounded border border-slate-700 text-slate-300">
                    <span className="flex items-center gap-1.5">
                      <Thermometer className="h-4 w-4 text-amber-400" />
                      <span>Trapped moisture thermal capacitance signature: 86.4% probability</span>
                    </span>
                    <span className="text-amber-400 font-bold">CUI WINDOW</span>
                  </div>
                </div>
              )}

              {/* PEC Grid Overlay: uses ndt_screening.jpeg */}
              {activeDemoMode === 'pec' && (
                <div className="relative w-full h-full">
                  <img
                    src="/ndt_screening.jpeg"
                    alt="PEC NDT Field Screening"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/30" />

                  {/* Ultrasonic/PEC measurement callout */}
                  <div
                    className="absolute bg-slate-900/90 border border-blue-400 p-2 rounded text-xs font-mono text-white shadow-xl"
                    style={{ left: '35%', top: '35%' }}
                  >
                    <div className="text-[10px] text-blue-300">EDDYFI LYFT PEC SENSOR</div>
                    <div className="text-sm font-bold text-white mt-0.5">Liftoff: 50.8mm</div>
                    <div className="text-xs text-amber-400 font-bold">Rem. Wall: 7.07mm (55.7%)</div>
                  </div>

                  <div className="absolute top-3 right-3 font-mono text-[11px] text-blue-300 bg-slate-900/90 px-2.5 py-1 rounded border border-slate-700 flex items-center gap-2">
                    <span>WALL PROFILE:</span>
                    <div className="flex gap-1">
                      <span className="bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded">12.5mm</span>
                      <span className="bg-amber-600/80 text-white px-1.5 py-0.5 rounded">9.2mm</span>
                      <span className="bg-red-600 text-white px-1.5 py-0.5 rounded font-bold">7.07mm</span>
                    </div>
                  </div>

                  <div className="absolute bottom-3 inset-x-3 flex items-center justify-between text-xs font-mono bg-slate-900/90 p-2 rounded border border-slate-700 text-slate-300">
                    <span className="flex items-center gap-1.5">
                      <Zap className="h-4 w-4 text-blue-400" />
                      <span>Through-jacketing screening: Min wall 7.07mm (44.3% local loss)</span>
                    </span>
                    <span className="text-blue-400 font-bold">NO STRIP REQ</span>
                  </div>
                </div>
              )}

              {/* API 579 FFS Engine Overlay: uses piping_isometric.jpeg */}
              {activeDemoMode === 'ffs' && (
                <div className="relative w-full h-full">
                  <img
                    src="/piping_isometric.jpeg"
                    alt="3D Piping Digital Twin Isometric"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-transparent to-slate-950/40" />

                  <div className="absolute top-3 left-3 font-mono text-[11px] text-green-300 bg-slate-900/90 px-2.5 py-1 rounded border border-slate-700">
                    API 579-1 / ASME FFS-1 PART 5 // REFINERY-DT-7734
                  </div>

                  <div className="absolute bottom-3 inset-x-3 flex items-center justify-between text-xs font-mono bg-slate-900/90 p-2 rounded border border-slate-700 text-green-300">
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-green-400" />
                      <span>Deterministic RSF = 0.932 &gt; 0.900 (MAWP: 2.41 MPa Certified)</span>
                    </span>
                    <span className="text-green-400 font-bold">DISPOSITION: PASS</span>
                  </div>
                </div>
              )}
            </div>

            {/* Telemetry Details Footer Panel */}
            <div className="p-5 sm:p-6 bg-slate-900 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h4 className="text-base font-bold text-white tracking-wide">{currentModeData.title}</h4>
                  <p className="text-xs text-slate-400 mt-0.5">{currentModeData.description}</p>
                </div>
                <span
                  className={`telemetry-pill font-bold self-start sm:self-auto ${
                    currentModeData.badgeType === 'danger'
                      ? 'bg-red-950 text-red-400 border border-red-800'
                      : currentModeData.badgeType === 'warning'
                      ? 'bg-amber-950 text-amber-400 border border-amber-800'
                      : 'bg-green-950 text-green-400 border border-green-800'
                  }`}
                >
                  {currentModeData.badge}
                </span>
              </div>

              {/* 3 Metric Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                {currentModeData.metrics.map((m, idx) => (
                  <div key={idx} className="bg-slate-800/80 p-3 rounded-lg border border-slate-700">
                    <div className="text-[10px] text-slate-400 uppercase font-mono">{m.label}</div>
                    <div className="text-base font-bold font-mono text-white mt-0.5">{m.value}</div>
                    {m.note && <div className="text-[11px] text-blue-400 font-mono mt-0.5">{m.note}</div>}
                  </div>
                ))}
              </div>

              {/* Deep engineering notes list */}
              <div className="pt-3 border-t border-slate-800/80 text-xs font-mono text-slate-400 space-y-1.5">
                {currentModeData.details.map((d, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="text-blue-500">▶</span>
                    <span>{d}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Scaffolding ROI Calculator */}
      <section id="calculator" className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold mb-3">
              <Calculator className="h-3.5 w-3.5" />
              <span>DETERMINISTIC COST MODEL</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              Interactive Scaffolding & Stripping ROI Calculator
            </h2>
            <p className="text-slate-600 mt-3 text-base">
              Calculate the direct financial savings of replacing blanket turnaround stripping with non-destructive multimodal screening.
            </p>
          </div>

          <div className="tech-panel p-6 sm:p-10 rounded-2xl max-w-5xl mx-auto shadow-lg border border-slate-200">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              
              {/* Sliders Form */}
              <div className="lg:col-span-6 space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-semibold text-slate-800">
                      Total Insulated Piping & Vessel Length
                    </label>
                    <span className="font-mono font-bold text-blue-600 text-sm bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                      {pipingKm} Kilometers
                    </span>
                  </div>
                  <input
                    type="range"
                    min={2}
                    max={50}
                    step={1}
                    value={pipingKm}
                    onChange={(e) => setPipingKm(Number(e.target.value))}
                    className="w-full accent-blue-600 cursor-pointer"
                  />
                  <div className="flex justify-between text-[11px] text-slate-400 font-mono mt-1">
                    <span>2 km (Small unit)</span>
                    <span>50 km (Full refinery)</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-semibold text-slate-800">
                      Annual Scaffolding & Turnaround Budget
                    </label>
                    <span className="font-mono font-bold text-blue-600 text-sm bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                      €{(scaffoldingBudget / 1000).toFixed(0)}k
                    </span>
                  </div>
                  <input
                    type="range"
                    min={300000}
                    max={5000000}
                    step={100000}
                    value={scaffoldingBudget}
                    onChange={(e) => setScaffoldingBudget(Number(e.target.value))}
                    className="w-full accent-blue-600 cursor-pointer"
                  />
                  <div className="flex justify-between text-[11px] text-slate-400 font-mono mt-1">
                    <span>€300,000</span>
                    <span>€5,000,000</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-semibold text-slate-800">
                      Current Blanket Stripping Scope
                    </label>
                    <span className="font-mono font-bold text-blue-600 text-sm bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                      {currentStripRate}% of circuits
                    </span>
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={50}
                    step={5}
                    value={currentStripRate}
                    onChange={(e) => setCurrentStripRate(Number(e.target.value))}
                    className="w-full accent-blue-600 cursor-pointer"
                  />
                  <div className="flex justify-between text-[11px] text-slate-400 font-mono mt-1">
                    <span>10% (Conservative)</span>
                    <span>50% (High regulatory audit)</span>
                  </div>
                </div>
              </div>

              {/* Calculated Results Block */}
              <div className="lg:col-span-6 bg-slate-900 text-white p-6 sm:p-8 rounded-xl border border-slate-800 shadow-xl space-y-6">
                <div className="border-b border-slate-800 pb-4">
                  <span className="text-xs font-mono text-green-400 uppercase tracking-wider block mb-1">
                    PROJECTED NET CAMPAIGN SAVINGS
                  </span>
                  <div className="text-4xl sm:text-5xl font-extrabold font-mono text-white">
                    €{estimatedSavings.toLocaleString()}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Direct avoidance of blind scaffolding erection, stripping, and hazardous insulation disposal.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-800/80 p-3.5 rounded border border-slate-700">
                    <div className="text-[11px] font-mono text-slate-400">UNNECESSARY STRIPS PREVENTED</div>
                    <div className="text-2xl font-bold font-mono text-amber-400 mt-1">
                      {unnecessaryStrippedLocations} locations
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Dry sound insulation kept intact</div>
                  </div>

                  <div className="bg-slate-800/80 p-3.5 rounded border border-slate-700">
                    <div className="text-[11px] font-mono text-slate-400">HIGH-RISK MAN-HOURS SAVED</div>
                    <div className="text-2xl font-bold font-mono text-blue-400 mt-1">
                      {scaffoldingHoursSaved.toLocaleString()} hrs
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Reduced working-at-height exposure</div>
                  </div>
                </div>

                <div className="pt-2 flex items-center justify-between">
                  <div className="text-xs text-slate-400 font-mono">
                    Estimated Platform Payback: <strong className="text-green-400">&lt; 45 Days</strong>
                  </div>
                  <Link to="/login" className="btn-primary text-xs py-2 px-3">
                    Start Pilot Campaign
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Platform Capabilities Section */}
      <section id="features" className="py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold mb-3">
              <Cpu className="h-3.5 w-3.5" />
              <span>SIX INTEGRATED PILLARS</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Engineering-Grade Multimodal Architecture
            </h2>
            <p className="text-slate-600 mt-3 text-lg">
              Designed specifically for mechanical integrity managers, inspection engineers, and plant operators.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((item, index) => (
              <div
                key={index}
                className="card-hover p-6 rounded-xl border border-slate-200 bg-white flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-12 w-12 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <span className="telemetry-pill bg-slate-100 text-slate-700 font-semibold border border-slate-200">
                      {item.badge}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">{item.description}</p>
                </div>

                <div className="pt-4 border-t border-slate-100 space-y-1.5 font-mono text-xs text-slate-700">
                  {item.points.map((pt, pIdx) => (
                    <div key={pIdx} className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                      <span>{pt}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The 4-Stage Closed-Loop Integrity Pipeline */}
      <section id="workflow" className="py-24 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold mb-3">
              <Layers className="h-3.5 w-3.5" />
              <span>THE INTEGRITY PIPELINE</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Deterministic Closed-Loop Execution
            </h2>
            <p className="text-slate-600 mt-3 text-lg">
              From raw sensor upload to work order dispatch in four verifiable steps.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {workflowSteps.map((step, idx) => (
              <div key={idx} className="card p-6 bg-white border border-slate-200 rounded-xl relative flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-4xl font-black font-mono text-blue-200">{step.num}</span>
                    <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{step.title}</h3>
                  <div className="text-xs font-mono font-semibold text-blue-700 mb-3">{step.subtitle}</div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">{step.description}</p>
                </div>

                <div className="pt-3 border-t border-slate-100">
                  <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider block mb-1">
                    SPECIFICATION
                  </span>
                  <p className="text-xs font-mono text-slate-700 font-semibold">{step.spec}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Traditional Inspection vs. InsuScan AI
            </h2>
            <p className="text-slate-600 mt-3 text-lg">
              Why leading refineries and petrochemical complexes are switching from blanket stripping to intelligent NDT.
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-slate-100/80 border-b border-slate-200">
                  <th className="p-4 font-bold text-slate-800 w-1/4">Evaluation Factor</th>
                  <th className="p-4 font-bold text-red-800 bg-red-50/50 w-3/8">Traditional Blanket Stripping</th>
                  <th className="p-4 font-bold text-blue-800 bg-blue-50/50 w-3/8">InsuScan AI Intelligent Screening</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {comparisonData.map((row, index) => (
                  <tr key={index} className="hover:bg-slate-50/80 transition-colors">
                    <td className="p-4 font-semibold text-slate-800">{row.feature}</td>
                    <td className="p-4 text-slate-600 bg-red-50/10">{row.traditional}</td>
                    <td className="p-4 text-slate-900 font-medium bg-blue-50/10 flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>{row.insuscan}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Regulatory Standards Grid */}
      <section id="standards" className="py-16 bg-slate-900 text-white border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-mono text-blue-400 tracking-widest uppercase mb-2">
              COMPLIANCE ARCHITECTURE
            </div>
            <h3 className="text-2xl font-bold">Built According to International Mechanical Codes</h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-center font-mono">
            <div className="p-4 rounded-lg bg-slate-800/60 border border-slate-700">
              <div className="text-sm font-bold text-white">API 579-1</div>
              <div className="text-[11px] text-slate-400 mt-1">Fitness-for-Service</div>
            </div>
            <div className="p-4 rounded-lg bg-slate-800/60 border border-slate-700">
              <div className="text-sm font-bold text-white">ASME FFS-1</div>
              <div className="text-[11px] text-slate-400 mt-1">Post-Construction</div>
            </div>
            <div className="p-4 rounded-lg bg-slate-800/60 border border-slate-700">
              <div className="text-sm font-bold text-white">API RP 583</div>
              <div className="text-[11px] text-slate-400 mt-1">CUI & CUF Practice</div>
            </div>
            <div className="p-4 rounded-lg bg-slate-800/60 border border-slate-700">
              <div className="text-sm font-bold text-white">API 581</div>
              <div className="text-[11px] text-slate-400 mt-1">Risk-Based Insp.</div>
            </div>
            <div className="p-4 rounded-lg bg-slate-800/60 border border-slate-700">
              <div className="text-sm font-bold text-white">NACE SP0198</div>
              <div className="text-[11px] text-slate-400 mt-1">Corrosion Control</div>
            </div>
            <div className="p-4 rounded-lg bg-slate-800/60 border border-slate-700">
              <div className="text-sm font-bold text-white">ASTM C1060</div>
              <div className="text-[11px] text-slate-400 mt-1">Thermal Thermography</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Engineering & Deployment FAQ</h2>
            <p className="text-slate-600 mt-2">Technical clarity for integrity specialists and IT architects.</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = openFaqIndex === index;
              return (
                <div
                  key={index}
                  className="card overflow-hidden bg-white border border-slate-200 rounded-lg transition-all"
                >
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                    className="w-full text-left p-5 flex items-center justify-between gap-4 font-semibold text-slate-900 hover:text-blue-600"
                  >
                    <span>{faq.q}</span>
                    <ChevronDown className={`h-5 w-5 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180 text-blue-600' : 'text-slate-400'}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final Call to Action */}
      <section className="py-20 bg-gradient-to-r from-blue-700 via-blue-800 to-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Ready to Protect Assets and Eliminate Unnecessary Scaffolding?
          </h2>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto">
            Schedule a technical screening pilot on your next plant turnaround or test our deterministic FFS calculation suite.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <Link to="/login" className="btn bg-white text-blue-800 hover:bg-blue-50 font-bold px-6 py-3 rounded-lg text-base shadow-lg">
              Launch Platform Free Trial
              <ArrowRight className="h-5 w-5 ml-1" />
            </Link>
            <Link to="/docs" className="btn border border-blue-300 text-white hover:bg-white/10 px-6 py-3 rounded-lg text-base">
              Explore API Reference & Formulas
            </Link>
          </div>
        </div>
      </section>

      {/* Industrial Technical Footer */}
      <footer className="bg-slate-950 text-slate-400 py-14 text-sm border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 pb-10 border-b border-slate-800">
            <div>
              <div className="flex items-center gap-2.5 mb-3">
                <img src="/logo.png" alt="InsuScan AI" className="h-7 w-7 object-contain brightness-110" />
                <span className="text-lg font-bold text-white tracking-tight">InsuScan AI</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed font-mono">
                Multimodal NDT screening & deterministic mechanical integrity software for high-hazard industrial process assets.
              </p>
              <div className="mt-4 font-mono text-[11px] text-slate-500">
                KERNEL BUILD: 2026.09-REV4
              </div>
            </div>

            <div>
              <h4 className="text-xs font-mono uppercase tracking-wider text-slate-200 font-bold mb-3">
                Analysis Engines
              </h4>
              <ul className="space-y-2 text-xs font-mono">
                <li><Link to="/tools/ffs" className="hover:text-white transition-colors">API 579-1 FFS Part 5</Link></li>
                <li><Link to="/tools/ips" className="hover:text-white transition-colors">Priority Score (IPS)</Link></li>
                <li><Link to="/tools/cladding" className="hover:text-white transition-colors">Cladding Defect AI</Link></li>
                <li><Link to="/tools/thermal" className="hover:text-white transition-colors">Radiometric Thermography</Link></li>
                <li><Link to="/tools/pdf-parser" className="hover:text-white transition-colors">PDF OCR Parser</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-mono uppercase tracking-wider text-slate-200 font-bold mb-3">
                Integrations & APIs
              </h4>
              <ul className="space-y-2 text-xs font-mono">
                <li><Link to="/cmms" className="hover:text-white transition-colors">SAP PM Work Orders</Link></li>
                <li><Link to="/cmms" className="hover:text-white transition-colors">IBM Maximo Two-Way</Link></li>
                <li><Link to="/docs" className="hover:text-white transition-colors">REST / Webhook Interface</Link></li>
                <li><Link to="/docs" className="hover:text-white transition-colors">Offline Edge Deployment</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-mono uppercase tracking-wider text-slate-200 font-bold mb-3">
                Engineering Governance
              </h4>
              <ul className="space-y-2 text-xs font-mono">
                <li><span className="text-slate-500">ASME FFS-1 / API 579 Compliant</span></li>
                <li><span className="text-slate-500">NACE SP0198 Guidelines</span></li>
                <li><Link to="/faq" className="hover:text-white transition-colors">Verification Methodology</Link></li>
                <li><Link to="/docs" className="hover:text-white transition-colors">Security & Data Ownership</Link></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-mono text-slate-500">
            <div>
              © {new Date().getFullYear()} InsuScan AI Inc. Deterministic NDT Asset Integrity. All rights reserved.
            </div>
            <div className="flex gap-6">
              <Link to="/docs" className="hover:text-slate-400">Documentation</Link>
              <Link to="/faq" className="hover:text-slate-400">FAQ</Link>
              <Link to="/login" className="hover:text-slate-400">Operator Login</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
